/*
 * Copyright (c) 2022 Puratos. All rights reserved.
 */

import React, { Component } from 'react';
import { ConnectedRouter } from 'connected-react-router';
import { Redirect, Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import { fetchMyPuratosMapping, fetchSidebar } from "../../../../actions/my-puratos/dashboard.action";
import { fetchI18n } from "../../../../actions/my-puratos/i18n.action";
import { getRoute, isMappingLoaded, PageRoutes } from "../../../../api/utils/aemEndpointUtil";
import { fetchHomePageModel } from "../../../../actions/my-puratos/aem.action";
import { isLoggedIn } from "../../../../utils/my-puratos/roleUtils";
import { browserHistory } from "../../../../config/redux.store";
import { I18NProvider } from "../../hoc/withI18N";
import { initializeApplication } from "../../../../actions/my-puratos/configuration.action";
import { initializeCart } from "../../../../actions/cart";
import ScrollToTop from "../../hoc/ScrollToTop";
import EmptyPage from "../../templates/EmptyPage";

const AccountSettings = React.lazy(() => import(/* webpackChunkName: "myPuratos-account" */ "../account-settings/AccountSettings"));
const Cart = React.lazy(() => import(/* webpackChunkName: "myPuratos-cart" */ "../cart/Cart"));
const Chat = React.lazy(() => import(/* webpackChunkName: "myPuratos-chat" */ "../chat/Chat"));
const Checkout = React.lazy(() => import(/* webpackChunkName: "myPuratos-checkout" */ "../checkout/Checkout"));
const Contact = React.lazy(() => import(/* webpackChunkName: "myPuratos-contact" */ "../contact/Contact"));
const Contracts = React.lazy(() => import(/* webpackChunkName: "myPuratos-contracts" */ "../contracts/Contracts"));
const Dashboard = React.lazy(() => import(/* webpackChunkName: "myPuratos-dashboard" */ "../dashboard/Dashboard"));
const DeliveryNotes = React.lazy(() => import(/* webpackChunkName: "myPuratos-notes" */ "../delivery-notes/DeliveryNotes"));
const Documents = React.lazy(() => import(/* webpackChunkName: "myPuratos-documents" */ "../documents/Documents"));
const ExcelImport = React.lazy(() => import(/* webpackChunkName: "myPuratos-excel" */ "../excel-import/ExcelImport"));
const FavoriteRecipes = React.lazy(() => import(/* webpackChunkName: "myPuratos-recipes" */ "../favorite-recipes/FavoriteRecipes"));
const Invoices = React.lazy(() => import(/* webpackChunkName: "myPuratos-invoices" */ "../invoices/Invoices"));
const MyEvents = React.lazy(() => import(/* webpackChunkName: "myPuratos-events" */ "../my-events/MyEvents"));
const MyProducts = React.lazy(() => import(/* webpackChunkName: "myPuratos-myProducts" */ "../my-products/MyProducts"));
const Orders = React.lazy(() => import(/* webpackChunkName: "myPuratos-orders" */ "../orders/Orders"));
const Promotions = React.lazy(() => import(/* webpackChunkName: "myPuratos-promotions" */ "../promotions/Promotions"));
const Register = React.lazy(() => import(/* webpackChunkName: "myPuratos-register" */ "../register/Register"));
const SignUp = React.lazy(() => import(/* webpackChunkName: "myPuratos-signUp" */ "../sign-up/SignUp"));

class Main extends Component {

  componentDidMount() {
    const { mappingmodel } = this.props;
    this.props.fetchMyPuratosMapping(mappingmodel);
    this.props.initializeApplication();
  }

  componentDidUpdate(oldProps) {
    const { mapping } = this.props;
    if (oldProps.mapping !== mapping) {
      if (isLoggedIn()) {
        this.props.initializeCart();
      }
      this.props.initializeApplication();
      this.props.fetchSidebar(mapping);
      this.props.fetchI18n(mapping);
      this.props.fetchHomePageModel(mapping);
    }
  }

  render() {
    const { sidebar, mapping, context, i18n } = this.props;

    const myProductsRoute = getRoute(mapping, PageRoutes.myProducts);
    const promotionRoute = getRoute(mapping, PageRoutes.promotions);
    const chatRoute = getRoute(mapping, PageRoutes.chat);
    const contactRoute = getRoute(mapping, PageRoutes.contact);
    const cartRoute = getRoute(mapping, PageRoutes.cart);
    const ordersRoute = getRoute(mapping, PageRoutes.orders);
    const registerRoute = getRoute(mapping, PageRoutes.register);
    const signUpRoute = getRoute(mapping, PageRoutes.signUp);
    const requestAccessRoute = getRoute(mapping, PageRoutes.requestAccess);
    const checkoutRoute = getRoute(mapping, PageRoutes.checkout);
    const allProductsRoute = getRoute(mapping, PageRoutes.allProducts);
    const accountSettingsRoute = getRoute(mapping, PageRoutes.accountSettings);
    const documentsRoute = getRoute(mapping, PageRoutes.documents);
    const invoicesRoute = getRoute(mapping, PageRoutes.invoices);
    const favoriteRecipesRoute = getRoute(mapping, PageRoutes.favoriteRecipes);
    const dashboardRoute = getRoute(mapping, PageRoutes.dashboard);
    const myEventsRoute = getRoute(mapping, PageRoutes.myEvents);
    const excelImportRoute = getRoute(mapping, PageRoutes.excelImport);
    const deliveryNotesRoute = getRoute(mapping, PageRoutes.deliveryNotes);
    const contractsRoute = getRoute(mapping, PageRoutes.contracts);

    return (<React.Fragment>
      <I18NProvider>
        <ConnectedRouter history={browserHistory} context={context}>
          <ScrollToTop>
            {isMappingLoaded(mapping) ? <Switch>
              <Route path="/" exact={true}
                     render={() => sidebar.dashboardHome ? <Redirect to={sidebar.dashboardHome}/> : <Dashboard/>}/>

              {allProductsRoute && sidebar.productFinderPath &&
                <Route from={`/${allProductsRoute}`} render={() => {
                  window.location.pathname = sidebar.productFinderPath;
                  return null;
                }}/>}

              {myProductsRoute && <Route path={`/${myProductsRoute}`} component={MyProducts}/>}
              {accountSettingsRoute && <Route path={`/${accountSettingsRoute}`} component={AccountSettings}/>}
              {promotionRoute && <Route path={`/${promotionRoute}`} component={Promotions}/>}
              {contactRoute && <Route path={`/${contactRoute}`} component={Contact}/>}
              {chatRoute && <Route path={`/${chatRoute}`} component={Chat}/>}
              {checkoutRoute && <Route path={`/${checkoutRoute}`} component={Checkout}/>}
              {documentsRoute && <Route path={`/${documentsRoute}`} component={Documents}/>}
              {invoicesRoute && <Route path={`/${invoicesRoute}`} component={Invoices}/>}
              {ordersRoute && <Route path={`/${ordersRoute}`} component={Orders}/>}
              {deliveryNotesRoute && <Route path={`/${deliveryNotesRoute}`} component={DeliveryNotes}/>}
              {contractsRoute && <Route path={`/${contractsRoute}`} component={Contracts}/>}

              <Route path={`/${cartRoute}`} component={Cart}/>
              <Route path={`/${registerRoute}`} component={Register}/>
              <Route path={`/${signUpRoute}`} component={SignUp}/>
              <Route path={`/${requestAccessRoute}`} render={(props) => <SignUp {...props} newCustomer={false}/>}/>
              <Route path={`/${favoriteRecipesRoute}`} component={FavoriteRecipes}/>
              <Route path={`/${myEventsRoute}`} component={MyEvents}/>
              <Route path={`/${excelImportRoute}`} component={ExcelImport}/>
              <Route exact={true} path={`/${dashboardRoute}`} component={Dashboard}/>
              <Route
                render={() => sidebar.dashboardHome ? <Redirect to={`/${sidebar.dashboardHome}`}/> : <Dashboard/>}/>
            </Switch> : <EmptyPage mapping={mapping}/>}
          </ScrollToTop>
        </ConnectedRouter>
      </I18NProvider>
    </React.Fragment>)
  }
}

const mapStateToProps = (state) => ({
  sidebar: state.myPuratos_navigation.sidebar,
  i18n: state.myPuratos_i18n.i18n,
  mapping: state.myPuratos_dashboard.mapping,
});

const mapDispatchToProps = {
  fetchSidebar,
  fetchI18n,
  fetchMyPuratosMapping,
  fetchHomePageModel,
  initializeCart,
  initializeApplication
};

export default connect(mapStateToProps, mapDispatchToProps)(Main);
